import { Component } from '@theme/component';

/**
 * Garde-fou taille (demande client 22/09) : sur la fiche produit, aucune
 * taille n'est présélectionnée et l'ajout au panier est bloqué tant que le
 * client n'en a pas choisi une.
 *
 * Fonctionnement, sans toucher aux fichiers Horizon :
 * - une option « Sélectionner une taille » (vide, désactivée) est ajoutée en
 *   tête de la liste déroulante de l'option visée et sélectionnée par
 *   propriété. L'attribut `selected` du markup Horizon n'est pas modifié :
 *   le sélecteur de variantes s'en sert pour construire ses requêtes ;
 * - tant qu'aucune taille n'est choisie, un champ requis vide est posé dans le
 *   formulaire d'ajout : Horizon vérifie `form.checkValidity()` avant de jouer
 *   l'animation d'ajout (assets/product-form.js, `handleClick`), qui ne se
 *   joue donc plus. Le clic sur le bouton et la soumission sont ensuite
 *   interceptés au niveau du formulaire pour afficher le message ;
 * - la barre d'ajout collante joue sa propre animation dans son gestionnaire
 *   de clic (délégué au niveau du document, impossible à devancer) : tant
 *   qu'aucune taille n'est choisie, son bouton ne reçoit plus les clics
 *   (pointer-events) et la barre elle-même les capte pour afficher le message ;
 * - Horizon re-rend le sélecteur (morph) à chaque changement d'option : le
 *   sélecteur est observé et l'option vide est remise tant que rien n'est
 *   choisi.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} message - Le message d'erreur (rendu par le block).
 *
 * @extends Component<Refs>
 */
class MgSizeGate extends Component {
  requiredRefs = ['message'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {boolean} */
  #chosen = false;

  /** @type {HTMLInputElement | undefined} */
  #requiredField;

  connectedCallback() {
    super.connectedCallback();

    const form = this.#form();
    const picker = this.#picker();
    if (!form || !picker) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Lien partagé avec une variante précise (?variant=…) : la taille est voulue, pas de garde-fou
    if (new URL(window.location.href).searchParams.has('variant')) this.#chosen = true;

    this.#apply();

    // Choix d'une taille : levée du garde-fou
    picker.addEventListener(
      'change',
      (event) => {
        const select = this.#select();
        if (event.target !== select || !select) return;
        if (select.value !== '') {
          this.#chosen = true;
          this.#hideError();
          this.#release();
        }
      },
      { signal }
    );

    // Re-rendu du sélecteur par Horizon
    this.#observer = new MutationObserver(() => this.#apply());
    this.#observer.observe(picker, { childList: true, subtree: true });

    // Blocage avant la délégation d'Horizon (document), en phase de bouillonnement sur le formulaire
    form.addEventListener('click', this.#onClick, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });

    // Barre d'ajout collante (sticky-add-to-cart, hors .product-details)
    const sticky = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
    sticky?.addEventListener('click', this.#onStickyClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#release();
  }

  /** @returns {HTMLFormElement | null} */
  #form() {
    const details = this.closest('.product-details');
    return details?.querySelector('product-form-component:not(.mg-look__form) form') ?? null;
  }

  /** @returns {Element | null} */
  #picker() {
    return this.closest('.product-details')?.querySelector('variant-picker') ?? null;
  }

  /**
   * La liste déroulante de l'option visée (« Taille »), si elle offre un choix.
   * @returns {HTMLSelectElement | null}
   */
  #select() {
    const wanted = (this.dataset.option ?? '').trim().toLowerCase();
    for (const select of this.#picker()?.querySelectorAll('select') ?? []) {
      const option = select.querySelector('option[data-option-name]');
      const name = (option?.dataset.optionName ?? '').trim().toLowerCase();
      if (name === wanted && select.querySelectorAll('option[data-option-name]').length > 1) {
        return /** @type {HTMLSelectElement} */ (select);
      }
    }
    return null;
  }

  #apply() {
    if (this.#chosen) return;
    const select = this.#select();
    if (!select) return;

    this.#hold();

    let placeholder = select.querySelector('option[data-mg-placeholder]');
    if (!placeholder) {
      placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.hidden = true;
      placeholder.dataset.mgPlaceholder = '';
      placeholder.textContent = this.dataset.placeholder ?? '';
      select.prepend(placeholder);
    }
    if (select.value !== '') {
      select.value = '';
    }
  }

  /** Le bouton de la barre collante */
  #stickyButton() {
    return this.closest('.shopify-section')?.querySelector('.sticky-add-to-cart__button') ?? null;
  }

  /**
   * Bloque l'ajout : champ requis vide dans le formulaire (Horizon n'anime pas
   * un formulaire invalide) et bouton de la barre collante inerte.
   */
  #hold() {
    const form = this.#form();
    if (form && !this.#requiredField) {
      const field = document.createElement('input');
      field.type = 'text';
      field.required = true;
      field.value = '';
      field.tabIndex = -1;
      field.setAttribute('aria-hidden', 'true');
      field.hidden = true;
      field.dataset.mgSizeRequired = '';
      form.append(field);
      this.#requiredField = field;
    }
    const sticky = this.#stickyButton();
    if (sticky instanceof HTMLElement) {
      sticky.style.pointerEvents = 'none';
      sticky.setAttribute('aria-disabled', 'true');
    }
  }

  /** Libère l'ajout une fois la taille choisie */
  #release() {
    this.#requiredField?.remove();
    this.#requiredField = undefined;
    const sticky = this.#stickyButton();
    if (sticky instanceof HTMLElement) {
      sticky.style.pointerEvents = '';
      sticky.removeAttribute('aria-disabled');
    }
  }

  /** @returns {boolean} */
  #isBlocked() {
    if (this.#chosen) return false;
    const select = this.#select();
    return !!select && select.value === '';
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[name="add"]') : null;
    if (!button || !this.#isBlocked()) return;
    event.preventDefault();
    event.stopPropagation();
    // La barre collante marque le bouton comme « piloté » avant de cliquer : on le libère
    if (button instanceof HTMLElement) button.dataset.puppet = 'false';
    this.#showError();
  };

  /**
   * Clic dans la barre collante : le bouton étant inerte tant qu'aucune taille
   * n'est choisie, le clic arrive sur la barre ; s'il tombe sur le bouton, on
   * affiche le message.
   * @param {MouseEvent} event
   */
  #onStickyClick = (event) => {
    if (!this.#isBlocked()) return;
    const button = this.#stickyButton();
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const onButton =
      (event.target instanceof Element && event.target.closest('.sticky-add-to-cart__button')) ||
      (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom);
    if (!onButton) return;
    event.preventDefault();
    event.stopPropagation();
    this.#showError();
  };

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (!this.#isBlocked()) return;
    event.preventDefault();
    event.stopPropagation();
    this.#showError();
  };

  #showError() {
    const select = this.#select();
    const { message } = this.refs;
    message.hidden = false;
    select?.closest('.variant-option')?.classList.add('mg-size-gate--error');
    select?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    select?.focus({ preventScroll: true });
  }

  #hideError() {
    this.refs.message.hidden = true;
    this.#picker()?.querySelector('.mg-size-gate--error')?.classList.remove('mg-size-gate--error');
  }
}

if (!customElements.get('mg-size-gate')) {
  customElements.define('mg-size-gate', MgSizeGate);
}
